class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);





/* === FINAL OPTIMIZED PROGRESS BAR + UPSELL LOGIC === */

async function getCart() {
  const res = await fetch('/cart.js');
  return res.json();
}

/* --- Core Progress Update --- */
function updateCartProgress(cart) {
  if (!window.cartFreeGiftEnabled) return;

  const threshold = parseFloat(window.cartFreeGiftThreshold || 500);
  const cartTotal = cart.total_price / 100;
  const remaining = Math.max(threshold - cartTotal, 0).toFixed(2);
  const progress = Math.min((cartTotal / threshold) * 100, 100);

  const bar = document.querySelector('#CartProgressBar');
  const msg = document.querySelector('#CartProgressMessage');
  if (bar) bar.style.width = `${progress}%`;

  if (msg) {
    msg.textContent =
      cartTotal >= threshold
        ? 'You qualify for Free T-Shirt!'
        : `Only $${remaining} away from a Free T-Shirt!!`;
  }

  toggleUpsellVisibility(cart);
}

/* --- Core Upsell Visibility Logic --- */
function toggleUpsellVisibility(cart) {
  const upsell = document.querySelector('#CartDrawerUpsell');
  if (!upsell) return;

  const threshold = parseFloat(window.cartFreeGiftThreshold || 500);
  const handle = (window.cartUpsellProductHandle || '').toLowerCase();
  const cartTotal = cart.total_price / 100;

  // Extract handles safely from both legacy and modern formats
  const handles = cart.items.map((item) => {
    if (item.handle) return item.handle.toLowerCase();
    if (item.product && item.product.handle) return item.product.handle.toLowerCase();
    if (item.url) {
      const match = item.url.match(/\/products\/([\w\-]+)/);
      return match ? match[1].toLowerCase() : '';
    }
    return '';
  });

  const upsellInCart = handles.includes(handle);

  // Toggle visibility
  if (cartTotal < threshold || upsellInCart) {
    upsell.classList.add('hidden');
  } else {
    upsell.classList.remove('hidden');
  }
}

/* --- Re-inject Upsell After Drawer Render --- */
window.customElements.whenDefined('cart-drawer-items').then(() => {
  const drawerItems = document.querySelector('cart-drawer-items');
  if (!drawerItems) return;

  let lastInnerHTML = '';

  const observer = new MutationObserver(async (mutations) => {
    const inner = drawerItems.querySelector('.drawer__inner');
    if (!inner) return;

    // Detect complete innerHTML replacement
    if (inner.innerHTML !== lastInnerHTML) {
      lastInnerHTML = inner.innerHTML;

      // Wait one frame for DOM stability
      requestAnimationFrame(async () => {
        const cart = await getCart();
        updateCartProgress(cart);
      });
    }
  });

  observer.observe(drawerItems, { childList: true, subtree: true });
});

/* --- Handle Add to Cart for Upsell (with Drawer Refresh) --- */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('#UpsellAddButton');
  if (!btn) return;

  const select = document.querySelector('#UpsellVariantSelector');
  if (!select) return;

  const variantId = select.value;
  btn.classList.add('loading');
  btn.textContent = 'Adding…';

  try {
    // 1️⃣ Add product via AJAX
    const addRes = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    });

    if (!addRes.ok) throw new Error('Add failed');

    // 2️⃣ Fetch fresh cart data
    const cart = await getCart();
    updateCartProgress(cart);

    // 3️⃣ Re-render cart drawer sections (native Shopify method)
    const sectionsToRender = ['cart-drawer', 'cart-icon-bubble'];
    const res = await fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender.join(',')}`
    );
    const json = await res.json();

    // 4️⃣ Replace the inner drawer HTML
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      drawer.getSectionsToRender().forEach((section) => {
        const element = document.querySelector(section.selector);
        if (element && json[section.id]) {
          element.innerHTML = new DOMParser()
            .parseFromString(json[section.id], 'text/html')
            .querySelector(section.selector).innerHTML;
        }
      });
    }

    // 5️⃣ Reopen the drawer after updating
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }

    // 6️⃣ Update progress bar + hide upsell if needed
    updateCartProgress(cart);

    btn.textContent = 'Added!';
  } catch (err) {
    console.error('Upsell add error', err);
    btn.textContent = 'Error';
  } finally {
    btn.classList.remove('loading');
    setTimeout(() => (btn.textContent = 'Add to cart'), 1500);
  }
});

/* --- Initial Render --- */
document.addEventListener('DOMContentLoaded', async () => {
  if (!window.cartFreeGiftEnabled) return;
  const cart = await getCart();
  updateCartProgress(cart);
});

/* --- Intercept Shopify Fetch Updates --- */
const originalFetch = window.fetch;
window.fetch = async (...args) => {
  const res = await originalFetch(...args);

  if (
    args[0].includes('/cart/add') ||
    args[0].includes('/cart/change') ||
    args[0].includes('/cart/update')
  ) {
    res
      .clone()
      .json()
      .then(updateCartProgress)
      .catch(async () => {
        const cart = await getCart();
        updateCartProgress(cart);
      });
  }

  return res;
};
